import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './ManagerChats.css';

const ManagerChats = ({ manager, assignedEmployees }) => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const messagesEndRef = useRef(null);

  // Fetch chats
  useEffect(() => {
    if (manager) {
      fetchChats();
    }
  }, [manager]);

  // Fetch messages for selected chat
  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat._id);
      markAsRead(selectedChat._id);
    }
  }, [selectedChat]);

  // Scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChats = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('managerToken');
      
      const response = await axios.get(`${API_BASE_URL}/api/chats/manager/${manager._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success) {
        const chatData = response.data.data || [];
        
        // Enrich chat data
        const enrichedChats = await Promise.all(
          chatData.map(async (chat) => {
            let employeeInfo = null;
            
            if (chat.employeeInfo) {
              employeeInfo = chat.employeeInfo;
            } else if (chat.metadata?.employeeId) {
              employeeInfo = chat.metadata.employeeId;
            } else {
              // Try to get employee info from participants
              const otherParticipant = chat.participants?.find(p => 
                p.userType === 'employee'
              );
              
              if (otherParticipant) {
                try {
                  const empResponse = await axios.get(
                    `${API_BASE_URL}/api/employees/${otherParticipant.userId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  
                  if (empResponse.data.success) {
                    employeeInfo = empResponse.data.data;
                  }
                } catch (error) {
                  console.error('Error fetching employee:', error);
                }
              }
            }
            
            return {
              ...chat,
              employeeInfo: employeeInfo,
              _id: chat._id,
              unreadCount: chat.unreadCount || 0,
              lastMessage: chat.lastMessage || 'Start a conversation',
              lastMessageTime: chat.lastMessageTime || chat.updatedAt
            };
          })
        );
        
        setChats(enrichedChats);
        
        // Auto-select first chat
        if (!selectedChat && enrichedChats.length > 0) {
          setSelectedChat(enrichedChats[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
      // Create placeholder chats from assigned employees
      createPlaceholderChats();
    } finally {
      setLoading(false);
    }
  };

  const createPlaceholderChats = () => {
    const placeholderChats = assignedEmployees.map(employee => ({
      _id: `temp_chat_${employee._id}`,
      participants: [
        { userId: manager._id, userType: 'manager' },
        { userId: employee._id, userType: 'employee' }
      ],
      employeeInfo: employee,
      lastMessage: 'Start a conversation',
      lastMessageTime: new Date().toISOString(),
      unreadCount: 0,
      isPlaceholder: true
    }));
    
    setChats(placeholderChats);
    if (!selectedChat && placeholderChats.length > 0) {
      setSelectedChat(placeholderChats[0]);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.get(`${API_BASE_URL}/api/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userId: manager._id }
      });
      
      if (response.data && response.data.success) {
        setMessages(response.data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Create placeholder messages
      if (selectedChat?.isPlaceholder) {
        setMessages([
          {
            _id: 'msg1',
            chatId: chatId,
            sender: selectedChat.employeeInfo?._id,
            senderType: 'employee',
            content: `Hello Manager ${manager.name}, this is ${selectedChat.employeeInfo?.name}`,
            createdAt: new Date(Date.now() - 3600000).toISOString(),
            read: true
          },
          {
            _id: 'msg2',
            chatId: chatId,
            sender: manager._id,
            senderType: 'manager',
            content: `Hi ${selectedChat.employeeInfo?.name}, how can I help you today?`,
            createdAt: new Date(Date.now() - 1800000).toISOString(),
            read: true
          }
        ]);
      }
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || sendingMessage) return;

    console.log('Attempting to send message...');
    console.log('Selected chat:', selectedChat);
    console.log('Manager ID:', manager._id);

    // Get receiver ID
    let receiverId;
    if (selectedChat.employeeInfo && selectedChat.employeeInfo._id) {
      receiverId = selectedChat.employeeInfo._id;
    } else {
      // Find receiver from participants
      const receiverParticipant = selectedChat.participants?.find(p => 
        p.userType === 'employee'
      );
      receiverId = receiverParticipant?.userId;
    }

    if (!receiverId) {
      console.error('No receiver found');
      alert('Cannot send message: Employee not found');
      return;
    }

    console.log('Receiver ID:', receiverId);

    const messageData = {
      content: newMessage.trim(),
      sender: manager._id,
      senderType: 'manager',
      receiver: receiverId,
      chatId: selectedChat._id?.startsWith('temp_') ? undefined : selectedChat._id
    };

    console.log('Message data:', messageData);

    // Optimistic update
    const tempMessage = {
      _id: `temp_${Date.now()}`,
      ...messageData,
      content: newMessage.trim(),
      createdAt: new Date().toISOString(),
      read: false,
      sending: true
    };

    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');
    setSendingMessage(true);

    try {
      const token = sessionStorage.getItem('managerToken');
      
      console.log('Sending API request...');
      const response = await axios.post(
        `${API_BASE_URL}/api/chats/send`,
        messageData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Response:', response.data);

      if (response.data && response.data.success) {
        // Replace temp message with real message
        setMessages(prev => 
          prev.map(msg => 
            msg._id === tempMessage._id 
              ? { ...response.data.data, _id: response.data.data._id } 
              : msg
          )
        );

        // Update chat list
        updateChatLastMessage(selectedChat._id, newMessage.trim());
      }
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Remove temp message
      setMessages(prev => prev.filter(msg => msg._id !== tempMessage._id));
      
      alert(`Failed to send message: ${error.response?.data?.message || error.message}`);
    } finally {
      setSendingMessage(false);
    }
  };

  const updateChatLastMessage = (chatId, messageContent) => {
    setChats(prev => 
      prev.map(chat => 
        chat._id === chatId 
          ? { 
              ...chat, 
              lastMessage: messageContent,
              lastMessageTime: new Date().toISOString()
            }
          : chat
      )
    );
  };

  const markAsRead = async (chatId) => {
    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.post(`${API_BASE_URL}/api/chats/${chatId}/mark-read`, {
        userId: manager._id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setChats(prev => 
        prev.map(chat => 
          chat._id === chatId 
            ? { ...chat, unreadCount: 0 }
            : chat
        )
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const filteredChats = chats.filter(chat => {
    if (!chat.employeeInfo) return false;
    
    const matchesSearch = chat.employeeInfo.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chat.employeeInfo.emp_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chat.lastMessage?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'unread') return matchesSearch && chat.unreadCount > 0;
    return matchesSearch;
  });

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else if (diffHours < 168) {
      return `${Math.floor(diffHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'short' 
      });
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const startNewChat = async (employee) => {
    try {
      const token = sessionStorage.getItem('managerToken');
      
      // Check if chat exists
      const existingChat = chats.find(chat => 
        chat.employeeInfo?._id === employee._id
      );

      if (existingChat) {
        setSelectedChat(existingChat);
        return;
      }

      // Create new chat via API
      const response = await axios.post(
        `${API_BASE_URL}/api/chats/get-or-create`,
        {
          managerId: manager._id,
          employeeId: employee._id
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const newChat = {
          ...response.data.data,
          employeeInfo: employee,
          unreadCount: 0
        };
        
        setChats(prev => [newChat, ...prev]);
        setSelectedChat(newChat);
      }
    } catch (error) {
      console.error('Error creating chat:', error);
      // Create local chat
      const newChat = {
        _id: `new_${employee._id}_${Date.now()}`,
        participants: [
          { userId: manager._id, userType: 'manager' },
          { userId: employee._id, userType: 'employee' }
        ],
        employeeInfo: employee,
        lastMessage: 'Start a conversation',
        lastMessageTime: new Date().toISOString(),
        unreadCount: 0,
        isNew: true
      };
      
      setChats(prev => [newChat, ...prev]);
      setSelectedChat(newChat);
    }
  };

  return (
    <div className="mc-container">
      {/* Header */}
      <div className="mc-header">
        <div className="mc-header-content">
          <h2 className="mc-title">
            <svg className="mc-title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Team Chat
          </h2>
          <p className="mc-subtitle">Communicate with your team members</p>
        </div>
      </div>

      <div className="mc-main-content">
        {/* Left Panel - Chat List */}
        <div className="mc-chat-list">
          {/* Search Bar */}
          <div className="mc-search-container">
            <div className="mc-search-input-wrapper">
              <svg className="mc-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search chats..."
                className="mc-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="mc-clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mc-tabs">
            <button 
              className={`mc-tab ${activeTab === 'all' ? 'mc-tab-active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All Chats
            </button>
            <button 
              className={`mc-tab ${activeTab === 'unread' ? 'mc-tab-active' : ''}`}
              onClick={() => setActiveTab('unread')}
            >
              Unread
              <span className="mc-unread-badge">
                {chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0)}
              </span>
            </button>
          </div>

          {/* Chat List */}
          <div className="mc-chat-items">
            {loading ? (
              <div className="mc-loading-chats">
                <div className="mc-spinner"></div>
                <p>Loading chats...</p>
              </div>
            ) : filteredChats.length === 0 ? (
              <div className="mc-empty-chats">
                <svg className="mc-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p>No chats found</p>
                <small>Start a conversation with your team members</small>
              </div>
            ) : (
              filteredChats.map(chat => (
                <div 
                  key={chat._id}
                  className={`mc-chat-item ${selectedChat?._id === chat._id ? 'mc-chat-item-active' : ''}`}
                  onClick={() => setSelectedChat(chat)}
                >
                  <div className="mc-chat-avatar">
                    {chat.employeeInfo?.profilePicture ? (
                      <img 
                        src={chat.employeeInfo.profilePicture} 
                        alt={chat.employeeInfo.name}
                        className="mc-chat-avatar-img"
                      />
                    ) : (
                      <div className="mc-chat-avatar-initial">
                        {getInitials(chat.employeeInfo?.name)}
                      </div>
                    )}
                    {chat.unreadCount > 0 && (
                      <span className="mc-chat-unread-indicator">{chat.unreadCount}</span>
                    )}
                  </div>
                  
                  <div className="mc-chat-info">
                    <div className="mc-chat-header">
                      <h4 className="mc-chat-name">
                        {chat.employeeInfo?.name || 'Unknown User'}
                        {chat.isNew && <span className="mc-new-badge">New</span>}
                      </h4>
                      <span className="mc-chat-time">
                        {formatTime(chat.lastMessageTime)}
                      </span>
                    </div>
                    
                    <p className="mc-chat-preview">
                      {chat.lastMessage || 'No messages yet'}
                    </p>
                    
                    <div className="mc-chat-meta">
                      <span className="mc-chat-id">
                        ID: {chat.employeeInfo?.emp_id || 'N/A'}
                      </span>
                      <span className="mc-chat-dept">
                        {chat.employeeInfo?.department || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* New Chat Button */}
          <div className="mc-new-chat-section">
            <button 
              className="mc-new-chat-btn"
              onClick={() => {
                if (assignedEmployees.length > 0) {
                  startNewChat(assignedEmployees[0]);
                }
              }}
            >
              <svg className="mc-new-chat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              New Chat
            </button>
          </div>
        </div>

        {/* Right Panel - Chat Messages */}
        <div className="mc-messages-container">
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className="mc-messages-header">
                <div className="mc-chat-header-info">
                  <div className="mc-chat-header-avatar">
                    {selectedChat.employeeInfo?.profilePicture ? (
                      <img 
                        src={selectedChat.employeeInfo.profilePicture} 
                        alt={selectedChat.employeeInfo.name}
                        className="mc-chat-header-avatar-img"
                      />
                    ) : (
                      <div className="mc-chat-header-avatar-initial">
                        {getInitials(selectedChat.employeeInfo?.name)}
                      </div>
                    )}
                  </div>
                  <div className="mc-chat-header-details">
                    <h3 className="mc-chat-header-name">
                      {selectedChat.employeeInfo?.name || 'Unknown User'}
                    </h3>
                    <div className="mc-chat-header-meta">
                      <span className="mc-chat-header-id">
                        ID: {selectedChat.employeeInfo?.emp_id || 'N/A'}
                      </span>
                      <span className="mc-chat-header-dept">
                        {selectedChat.employeeInfo?.department || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="mc-messages-area">
                {messages.length === 0 ? (
                  <div className="mc-empty-messages">
                    <svg className="mc-empty-messages-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h4>No messages yet</h4>
                    <p>Send a message to start the conversation</p>
                  </div>
                ) : (
                  <div className="mc-messages-list">
                    {messages.map((message) => {
                      const isManager = message.sender === manager._id || message.senderType === 'manager';
                      const isTemp = message._id?.startsWith('temp');
                      const isSending = message.sending;
                      
                      return (
                        <div 
                          key={message._id}
                          className={`mc-message ${isManager ? 'mc-message-outgoing' : 'mc-message-incoming'} ${isTemp ? 'mc-message-temp' : ''}`}
                        >
                          <div className="mc-message-content">
                            <p className="mc-message-text">{message.content}</p>
                            <div className="mc-message-meta">
                              <span className="mc-message-time">
                                {new Date(message.createdAt).toLocaleTimeString('en-IN', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {isManager && (
                                <span className="mc-message-status">
                                  {isTemp || isSending ? (
                                    <span className="mc-status-sending">⏳</span>
                                  ) : message.read ? (
                                    <span className="mc-status-read">✓✓</span>
                                  ) : (
                                    <span className="mc-status-sent">✓</span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="mc-message-input-container">
                <div className="mc-input-wrapper">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    className="mc-message-input"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={sendingMessage}
                  />
                  
                  <div className="mc-input-actions">
                    <button 
                      className="mc-send-btn"
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                    >
                      {sendingMessage ? (
                        <div className="mc-send-spinner"></div>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="mc-input-hint">
                  Press Enter to send • Shift+Enter for new line
                </div>
              </div>
            </>
          ) : (
            <div className="mc-no-chat-selected">
              <div className="mc-welcome-container">
                <svg className="mc-welcome-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3>Welcome to Team Chat</h3>
                <p>Select a conversation from the list or start a new chat with your team members.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerChats;